import { getOrCreateLocalGuest, getOrCreateLocalGuestSafe, readLocalGuest } from "./auth/localGuest";
import {
  AccountApiError,
  completeTikTokSignup,
  currentServerAccount,
  ensureServerGuest,
  loginServerAccount,
  logoutServerAccount,
  readServerAccount,
  recoverServerAccount,
  signupServerAccount,
  type ServerAccount,
} from "./auth/serverAccount";
import { mountTikTokLogin } from "./auth/tiktok";

const search = new URLSearchParams(window.location.search);
const requestedShot = search.get("shot");
const capture = requestedShot === "hero" || requestedShot === "ball" || search.get("capture") === "1";
const hash = new URLSearchParams(window.location.hash.slice(1));
const recoveryLogin = hash.get("login");
let tiktokSignupActive = search.get("tiktok") === "new";
const tiktokErrorReason = search.get("tiktok") === "error"
  ? (search.get("reason") ?? "tiktok_failed")
  : null;

const accountSession = document.querySelector<HTMLElement>("#account-session");
const accountPicker = document.querySelector<HTMLElement>("#account-picker");
const currentAccountLabel = document.querySelector<HTMLElement>("[data-current-account]");
const currentMemberLabel = document.querySelector<HTMLElement>("[data-current-member]");
const continueButton = document.querySelector<HTMLButtonElement>("#continue-entry");
const logoutButton = document.querySelector<HTMLButtonElement>("#logout-entry");
const guestButton = document.querySelector<HTMLButtonElement>("#guest-entry");
const guestLabel = document.querySelector<HTMLElement>("[data-guest-label]");
const entryStatus = document.querySelector<HTMLElement>("#entry-status");
const tiktokSignup = document.querySelector<HTMLElement>("#tiktok-signup");
const tiktokSignupForm = document.querySelector<HTMLFormElement>("#tiktok-signup-form");
const tiktokSignupUsername = document.querySelector<HTMLInputElement>("#tiktok-signup-username");
const tiktokSignupResult = document.querySelector<HTMLOutputElement>("#tiktok-signup-result");

const signupTab = document.querySelector<HTMLButtonElement>("#signup-tab");
const loginTab = document.querySelector<HTMLButtonElement>("#login-tab");
const signupView = document.querySelector<HTMLElement>("#signup-view");
const loginView = document.querySelector<HTMLElement>("#login-view");
const signupForm = document.querySelector<HTMLFormElement>("#signup-form");
const signupUsername = document.querySelector<HTMLInputElement>("#signup-username");
const signupPassword = document.querySelector<HTMLInputElement>("#signup-password");
const signupConfirm = document.querySelector<HTMLInputElement>("#signup-confirm");
const signupSubmit = document.querySelector<HTMLButtonElement>("#signup-submit");
const signupResult = document.querySelector<HTMLOutputElement>("#signup-result");
const loginForm = document.querySelector<HTMLFormElement>("#login-form");
const loginUsername = document.querySelector<HTMLInputElement>("#login-username");
const loginPassword = document.querySelector<HTMLInputElement>("#login-password");
const loginResult = document.querySelector<HTMLOutputElement>("#login-result");
const tiktokLoginSlot = document.querySelector<HTMLElement>("#tiktok-login-slot");
const recoveryForm = document.querySelector<HTMLFormElement>("#recovery-form");
const recoveryCodeInput = document.querySelector<HTMLInputElement>("#recovery-code-input");
const recoveryResult = document.querySelector<HTMLOutputElement>("#recovery-result");

const recoveryCard = document.querySelector<HTMLElement>("#recovery-card");
const recoveryKicker = document.querySelector<HTMLElement>("#recovery-kicker");
const recoveryLede = document.querySelector<HTMLElement>("#recovery-lede");
const issuedRecoveryCode = document.querySelector<HTMLElement>("#issued-recovery-code");
const copyRecoveryButton = document.querySelector<HTMLButtonElement>("#copy-recovery");
const downloadRecoveryButton = document.querySelector<HTMLButtonElement>("#download-recovery");
const recoveryContinueButton = document.querySelector<HTMLButtonElement>("#recovery-continue");
const recoverySaveResult = document.querySelector<HTMLOutputElement>("#recovery-save-result");

let loading = false;
let latestAccount: ServerAccount | null = readServerAccount();
let latestRecoveryCode = "";

void mountTikTokLogin(tiktokLoginSlot);

function memberNumber(account: ServerAccount): string {
  return account.memberNo ? `Member #${String(account.memberNo).padStart(3, "0")}` : "Guest save";
}

function guestEntryLabel(account: ServerAccount | null): string {
  const local = readLocalGuest();
  if (account?.accountKind === "guest") return `Continue as ${local?.handle ?? "guest"}`;
  // After sign-out this must read as what it is — an older separate save kept
  // by this browser — not as the member's progress having been wiped (P1-13).
  return local ? "Previous guest save from this browser" : "Play as guest";
}

function showStatus(message: string): void {
  if (entryStatus) entryStatus.textContent = message;
}

function friendlyError(error: unknown, fallback: string): string {
  return error instanceof AccountApiError || error instanceof Error ? error.message : fallback;
}

const TIKTOK_ERROR_MESSAGES: Record<string, string> = {
  tiktok_denied: "TikTok sign-in was cancelled. You can try again, or use a username and password.",
  tiktok_state_invalid: "That TikTok sign-in took too long. Tap Continue with TikTok to try again.",
  tiktok_account_linked: "That TikTok account is already connected to another member.",
  tiktok_deferred: "TikTok sign-in is not switched on yet. Use a username and password for now.",
};

function tiktokErrorMessage(reason: string): string {
  return TIKTOK_ERROR_MESSAGES[reason]
    ?? "TikTok sign-in did not finish. You can try again, or use a username and password.";
}

function renderAccount(account: ServerAccount | null): void {
  latestAccount = account;
  const persistent = account?.accountKind === "persistent" || account?.accountKind === "system";
  if (accountSession) accountSession.hidden = !persistent || tiktokSignupActive;
  if (accountPicker) accountPicker.hidden = persistent || tiktokSignupActive;
  if (tiktokSignup) tiktokSignup.hidden = !tiktokSignupActive;
  if (recoveryCard) recoveryCard.hidden = true;
  if (guestLabel) guestLabel.textContent = guestEntryLabel(account);
  if (persistent && account) {
    if (currentAccountLabel) currentAccountLabel.textContent = `@${account.handle}`;
    if (currentMemberLabel) currentMemberLabel.textContent = memberNumber(account);
  }
}

function setFormBusy(form: HTMLFormElement | null, busy: boolean): void {
  if (!form) return;
  form.setAttribute("aria-busy", String(busy));
  for (const control of form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button")) {
    control.disabled = busy;
  }
}

function switchAccountView(view: "signup" | "login", focus = true): void {
  const signupActive = view === "signup";
  if (signupView) signupView.hidden = !signupActive;
  if (loginView) loginView.hidden = signupActive;
  signupTab?.classList.toggle("is-active", signupActive);
  loginTab?.classList.toggle("is-active", !signupActive);
  signupTab?.setAttribute("aria-selected", String(signupActive));
  loginTab?.setAttribute("aria-selected", String(!signupActive));
  signupTab?.setAttribute("tabindex", signupActive ? "0" : "-1");
  loginTab?.setAttribute("tabindex", signupActive ? "-1" : "0");
  if (focus) (signupActive ? signupUsername : loginUsername)?.focus();
}

function showRecovery(account: ServerAccount, code: string, mode: "created" | "reissued" = "created"): void {
  latestAccount = account;
  latestRecoveryCode = code;
  if (accountSession) accountSession.hidden = true;
  if (accountPicker) accountPicker.hidden = true;
  if (recoveryCard) recoveryCard.hidden = false;
  if (recoveryKicker) recoveryKicker.textContent = mode === "reissued" ? "Signed back in" : "Account created";
  if (recoveryLede) {
    recoveryLede.textContent = mode === "reissued"
      ? "That code is used up now. This is your NEW recovery code — store it somewhere safe. It is shown only once."
      : "We can’t email a reset link, so store this somewhere safe. It is shown only once.";
  }
  if (issuedRecoveryCode) issuedRecoveryCode.textContent = code;
  showStatus(mode === "reissued"
    ? `Welcome back, @${account.handle}. Save your new recovery code before entering.`
    : `@${account.handle} is now ${memberNumber(account)}.`);
  recoveryCard?.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

async function openClubhouse(
  accountReady = false,
  statusNote = "",
  gameModule: Promise<typeof import("./main")> = import("./main"),
): Promise<void> {
  if (loading) return;
  loading = true;
  for (const button of [guestButton, continueButton, recoveryContinueButton]) {
    if (button) {
      button.disabled = true;
      button.dataset.loading = "true";
    }
  }
  if (guestLabel && !accountReady) guestLabel.textContent = "Opening the yard…";
  showStatus(statusNote ? `Loading Dog City. ${statusNote}` : "Loading Dog City.");

  try {
    // The game bundle is the largest part of first entry. Fetch/parse it while
    // the server establishes the guest identity, then mount only after that
    // identity is safely remembered so App cannot boot against a null account.
    if (!capture && !accountReady) {
      latestAccount = await ensureServerGuest(getOrCreateLocalGuest());
    }
    if (!capture && !window.__BDC_GAME_STATE_PRELOAD__) {
      window.__BDC_GAME_STATE_PRELOAD__ = fetch("/api/game/state", {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      }).then(async (response) => ({ status: response.status, payload: await response.json() }));
    }
    (await gameModule).mountGame();
  } catch (error) {
    loading = false;
    for (const button of [guestButton, continueButton, recoveryContinueButton]) {
      if (button) {
        button.disabled = false;
        delete button.dataset.loading;
      }
    }
    if (guestLabel) guestLabel.textContent = guestEntryLabel(latestAccount);
    showStatus(friendlyError(error, "Dog City did not open. Please try again."));
    console.error(error);
  }
}

signupTab?.addEventListener("click", () => switchAccountView("signup"));
loginTab?.addEventListener("click", () => switchAccountView("login"));
for (const tab of [signupTab, loginTab]) {
  tab?.addEventListener("keydown", (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const nextView = event.key === "ArrowLeft" || event.key === "Home" ? "signup" : "login";
    switchAccountView(nextView, false);
    (nextView === "signup" ? signupTab : loginTab)?.focus();
  });
}

for (const toggle of document.querySelectorAll<HTMLButtonElement>("[data-password-toggle]")) {
  toggle.addEventListener("click", () => {
    const inputs = (toggle.dataset.passwordToggle ?? "")
      .split(/\s+/)
      .map((id) => document.getElementById(id))
      .filter((input): input is HTMLInputElement => input instanceof HTMLInputElement);
    const show = inputs.some((input) => input.type === "password");
    for (const input of inputs) input.type = show ? "text" : "password";
    toggle.textContent = `${show ? "Hide" : "Show"} password${inputs.length > 1 ? "s" : ""}`;
  });
}

// Create-account validation (AUDIT P1-14): the submit button carries a real
// disabled state while required fields are empty — with a live hint so it is
// never a silent dead control — and invalid values always produce a visible
// message on submit. Native bubbles are not trusted: iOS Safari shows none.
const SIGNUP_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9_-]{1,18}[a-z0-9])?$/i;

function signupEmptyHint(): string | null {
  if (!signupUsername?.value.trim()) return "Choose a username to continue.";
  if (!signupPassword?.value) return "Add a password — 10 or more characters.";
  if (!signupConfirm?.value) return "Type your password again to confirm it.";
  return null;
}

function signupInvalidMessage(): { message: string; field: HTMLInputElement | null } | null {
  const username = signupUsername?.value.trim() ?? "";
  const password = signupPassword?.value ?? "";
  if (!username) {
    return { message: "Choose a username first — 3–20 letters, numbers, underscores, or hyphens.", field: signupUsername };
  }
  if (!SIGNUP_HANDLE_PATTERN.test(username)) {
    return {
      message: "Usernames are 3–20 letters, numbers, underscores, or hyphens, starting and ending with a letter or number.",
      field: signupUsername,
    };
  }
  if (!password) return { message: "Add a password — 10 or more characters.", field: signupPassword };
  if ([...password].length < 10) {
    return { message: "Passwords need at least 10 characters. A fun passphrase works great.", field: signupPassword };
  }
  if ([...password].length > 128) {
    return { message: "Passwords max out at 128 characters.", field: signupPassword };
  }
  if (password !== (signupConfirm?.value ?? "")) {
    return { message: "Those passwords do not match. Check both boxes.", field: signupConfirm };
  }
  return null;
}

let signupTouched = false;

function refreshSignupSubmit(): void {
  if (!signupSubmit) return;
  const emptyHint = signupEmptyHint();
  signupSubmit.disabled = emptyHint !== null;
  if (!signupResult) return;
  if (emptyHint === null) {
    // Only clear our own hints; submit-time errors persist until edited.
    if (signupResult.dataset.hint === "true") {
      signupResult.textContent = "";
      delete signupResult.dataset.hint;
    }
    return;
  }
  if (signupTouched) {
    signupResult.textContent = emptyHint;
    signupResult.dataset.hint = "true";
  }
}

for (const input of [signupUsername, signupPassword, signupConfirm]) {
  input?.addEventListener("input", () => {
    signupTouched = true;
    refreshSignupSubmit();
  });
  input?.addEventListener("blur", () => {
    if (signupUsername?.value || signupPassword?.value || signupConfirm?.value) signupTouched = true;
    refreshSignupSubmit();
  });
}
refreshSignupSubmit();

signupForm?.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    if (!signupUsername || !signupPassword || !signupConfirm || !signupResult) return;
    const invalid = signupInvalidMessage();
    if (invalid) {
      signupResult.textContent = invalid.message;
      delete signupResult.dataset.hint;
      invalid.field?.focus();
      return;
    }

    setFormBusy(signupForm, true);
    signupResult.textContent = "";
    showStatus("Creating your secure Clubhouse account…");
    try {
      await ensureServerGuest(getOrCreateLocalGuest());
      const result = await signupServerAccount(signupUsername.value.trim(), signupPassword.value);
      signupForm.reset();
      signupTouched = false;
      showRecovery(result.account, result.recoveryCode);
    } catch (error) {
      signupResult.textContent = friendlyError(error, "Account creation failed.");
      showStatus("Your guest save is safe. Fix the highlighted account issue and try again.");
    } finally {
      setFormBusy(signupForm, false);
      refreshSignupSubmit();
    }
  })();
});

loginForm?.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    if (!loginUsername || !loginPassword || !loginResult) return;
    setFormBusy(loginForm, true);
    loginResult.textContent = "";
    showStatus("Signing in…");
    try {
      const account = await loginServerAccount(loginUsername.value, loginPassword.value);
      loginForm.reset();
      renderAccount(account);
      await openClubhouse(true);
    } catch (error) {
      loginResult.textContent = friendlyError(error, "Sign-in failed.");
      showStatus("Check your username and password, then try again.");
    } finally {
      setFormBusy(loginForm, false);
    }
  })();
});

tiktokSignupForm?.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    if (!tiktokSignupUsername || !tiktokSignupResult) return;
    if (!tiktokSignupForm.reportValidity()) return;

    setFormBusy(tiktokSignupForm, true);
    tiktokSignupResult.textContent = "";
    showStatus("Creating your TikTok Clubhouse account…");
    try {
      const account = await completeTikTokSignup(tiktokSignupUsername.value);
      tiktokSignupForm.reset();
      tiktokSignupActive = false;
      const destination = new URL(window.location.href);
      destination.searchParams.delete("tiktok");
      history.replaceState(null, "", `${destination.pathname}${destination.search}${destination.hash}`);
      renderAccount(account);
      showStatus(`@${account.handle} is ready. TikTok is your sign-in.`);
      await openClubhouse(true);
    } catch (error) {
      tiktokSignupResult.textContent = friendlyError(error, "TikTok account creation failed.");
      showStatus("TikTok account creation could not be completed. Start again from TikTok sign-in.");
    } finally {
      setFormBusy(tiktokSignupForm, false);
    }
  })();
});

recoveryForm?.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    if (!recoveryCodeInput || !recoveryResult) return;
    setFormBusy(recoveryForm, true);
    recoveryResult.textContent = "";
    showStatus("Checking your recovery code…");
    try {
      const result = await recoverServerAccount(recoveryCodeInput.value);
      recoveryForm.reset();
      if (result.recoveryCode) {
        // The used code is burned; the player must save the reissued one
        // before entering the game.
        showRecovery(result.account, result.recoveryCode, "reissued");
      } else {
        renderAccount(result.account);
        await openClubhouse(true);
      }
    } catch (error) {
      recoveryResult.textContent = friendlyError(error, "Recovery failed.");
      showStatus("That recovery code was not accepted.");
    } finally {
      setFormBusy(recoveryForm, false);
    }
  })();
});

guestButton?.addEventListener("click", () => {
  void (async () => {
    try {
      showStatus("Opening a secure guest session…");
      const gameModule = import("./main");
      const localGuest = getOrCreateLocalGuestSafe();
      const account = await ensureServerGuest(localGuest.guest);
      latestAccount = account;
      if (guestLabel) guestLabel.textContent = guestEntryLabel(account);
      // Storage denied (private browsing / strict settings): play still works
      // on the server session, but this browser cannot remember the guest for
      // next time (W4 P1-7) — say so instead of hard-failing.
      await openClubhouse(
        true,
        localGuest.persisted
          ? ""
          : "Heads up: this browser blocks saved data, so this guest save may not be here next visit. Create a free account to keep progress.",
        gameModule,
      );
    } catch (error) {
      showStatus(friendlyError(error, "Guest sign-in failed."));
    }
  })();
});

continueButton?.addEventListener("click", () => {
  void openClubhouse(true);
});

logoutButton?.addEventListener("click", () => {
  void (async () => {
    logoutButton.disabled = true;
    showStatus("Signing out…");
    try {
      await logoutServerAccount();
      renderAccount(null);
      switchAccountView("login", false);
      showStatus("Signed out. Sign in again or start a separate guest save.");
    } catch (error) {
      showStatus(friendlyError(error, "Sign out failed."));
    } finally {
      logoutButton.disabled = false;
    }
  })();
});

copyRecoveryButton?.addEventListener("click", () => {
  void (async () => {
    if (!latestRecoveryCode || !recoverySaveResult) return;
    try {
      await navigator.clipboard.writeText(latestRecoveryCode);
      recoverySaveResult.textContent = "Recovery code copied.";
    } catch {
      recoverySaveResult.textContent = "Select the code above and copy it manually.";
    }
  })();
});

downloadRecoveryButton?.addEventListener("click", () => {
  if (!latestRecoveryCode || !latestAccount) return;
  const backup = [
    "Big Dog Club recovery backup",
    `Username: ${latestAccount.handle}`,
    `Recovery code: ${latestRecoveryCode}`,
    "",
    "Keep this private. Anyone with this code can access your Clubhouse account.",
  ].join("\n");
  const url = URL.createObjectURL(new Blob([backup], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `big-dog-club-recovery-${latestAccount.handle}.txt`;
  link.click();
  URL.revokeObjectURL(url);
  if (recoverySaveResult) recoverySaveResult.textContent = "Recovery backup downloaded.";
});

recoveryContinueButton?.addEventListener("click", () => {
  void openClubhouse(true);
});

// The HTML starts on account creation for a true first visit. Any locally
// remembered member or guest marks this as a returning device, where Sign in
// remains the useful default even if its server session has expired.
const returningDevice = latestAccount !== null || readLocalGuest() !== null;
switchAccountView(returningDevice ? "login" : "signup", false);
renderAccount(latestAccount);

if (tiktokErrorReason) {
  const destination = new URL(window.location.href);
  destination.searchParams.delete("tiktok");
  destination.searchParams.delete("reason");
  history.replaceState(null, "", `${destination.pathname}${destination.search}${destination.hash}`);
  showStatus(tiktokErrorMessage(tiktokErrorReason));
}

if (recoveryLogin && !capture) {
  void (async () => {
    try {
      showStatus("Opening your saved Clubhouse account…");
      const result = await recoverServerAccount(recoveryLogin);
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      if (result.recoveryCode) {
        showRecovery(result.account, result.recoveryCode, "reissued");
        return;
      }
      renderAccount(result.account);
      await openClubhouse(true);
    } catch (error) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      showStatus(friendlyError(error, "Private account link was rejected."));
    }
  })();
} else if (capture) {
  void openClubhouse(true);
} else {
  void (async () => {
    try {
      renderAccount(await currentServerAccount());
    } catch (error) {
      showStatus(friendlyError(error, "The membership desk is temporarily unavailable."));
    }
  })();
}
